import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, EventEmitter, Input, input, OnDestroy, OnInit, Output, signal, untracked } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { AccountService } from 'src/app/services/account/account.service';
import { SettingsService } from 'src/app/services/account/settings.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { AccountDialogComponent } from '../modals/account-dialog/account-dialog.component';
import { SubscriptionsDialogComponent } from '../modals/subscriptions-dialog/subscriptions-dialog.component';
import { HistoryDialogComponent } from '../modals/history-dialog/history-dialog.component';
import { SettingsDialogComponent } from '../modals/settings-dialog/settings-dialog.component';
import { WelcomeComponent } from '../modals/info/welcome/welcome.component';
import { FeedbackComponent } from '../modals/info/feedback/feedback.component';
import { PrivacyPolicyComponent } from '../modals/info/privacy-policy/privacy-policy.component';
import { EulaComponent } from '../modals/info/eula/eula.component';
import { TutorialService } from 'src/app/services/core/tutorial.service';
import { UtilsService } from 'src/app/services/utils/utils.service';

@Component({
  standalone: true,
  selector: 'app-profile-dropdown',
  templateUrl: './profile-dropdown.component.html',
  styleUrls: ['./profile-dropdown.component.scss'],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileDropdownComponent implements OnInit, OnDestroy {
  @Input() loadingMap: string = "";
  public readonly dropdownVisible = input<boolean>(false);
  /**
   * Class of the wrapper that holds both the toggle button and this dropdown. Presses inside it
   * don't close the menu (the toggle handles its own click); presses anywhere else do.
   */
  @Input() ownerClass: string = '';
  /**
   * The owner panel holds the open/closed state; this is emitted whenever the menu must close:
   * an entry was selected, the user pressed outside (map pan/tap, any other UI), or the auth
   * state changed (the menu always starts closed after login/logout).
   */
  @Output() closeRequested = new EventEmitter<void>();
  public readonly isAuthorized = signal<boolean>(false);
  public readonly infoSubMenuVisible = signal<boolean>(false);

  public readonly loginIconPath = signal<string>('');
  public readonly editIconPath = signal<string>('');
  public readonly subscriptionsIconPath = signal<string>('');
  public readonly historyIconPath = signal<string>('');
  public readonly settingsIconPath = signal<string>('');
  public readonly infoIconPath = signal<string>('');
  public readonly openArrowIconPath = signal<string>('');
  public readonly logoutIconPath = signal<string>('');
  public readonly backMenuIconPath = signal<string>('');
  public readonly welcomeIconPath = signal<string>('');
  public readonly feedbackIconPath = signal<string>('');
  public readonly tutorialIconPath = signal<string>('');
  public readonly privacyPolicyIconPath = signal<string>('');
  public readonly legalInfoIconPath = signal<string>('');

  private subscriptions: Subscription = new Subscription();
  private outsidePressSubscription: Subscription | null = null;

  constructor(
    public settingsService: SettingsService,
    public languageService: LanguageService,
    private stackedModalsService: StackedModalsService,
    private accountService: AccountService,
    private cdr: ChangeDetectorRef,
    public tutorialService: TutorialService,
    private utilsService: UtilsService
  ) {
    effect(() => {
      const visible = this.dropdownVisible();
      untracked(() => visible ? this.watchOutsidePress() : this.onClosed());
    });
  }

  // Like Google/Apple Maps: any press outside the menu closes it — the start of a map pan or tap,
  // any other UI button. `pointerdown` covers mouse and touch alike and fires before a drag begins;
  // listening in the capture phase means nothing downstream (the map canvas, a button handler)
  // can swallow it. The listener lives only while the menu is open.
  private watchOutsidePress(): void {
    this.outsidePressSubscription?.unsubscribe();
    this.outsidePressSubscription = fromEvent<PointerEvent>(document, 'pointerdown', { capture: true })
      .subscribe((event) => {
        if (!this.utilsService.isClassInParentChain(event, this.ownerClass)) {
          this.closeRequested.emit();
        }
      });
  }

  private onClosed(): void {
    this.outsidePressSubscription?.unsubscribe();
    this.outsidePressSubscription = null;
    // Reopening always shows the main menu, not a submenu left behind.
    this.infoSubMenuVisible.set(false);
  }

  ngOnInit(): void {
    this.subscriptions.add(this.settingsService.ObservableMap.subscribe((mapSettings) => {
      if (mapSettings) {
        const skin = mapSettings.Skin;
        const iconBase = `/assets/icons/${skin}/ui`;

        this.loginIconPath.set(`${iconBase}/login_icon.svg`);
        this.editIconPath.set(`${iconBase}/edit_icon.svg`);
        this.subscriptionsIconPath.set(`${iconBase}/menu_icon_subscriptions.svg`);
        this.historyIconPath.set(`${iconBase}/menu_icon_history.svg`);
        this.settingsIconPath.set(`${iconBase}/settings_icon.svg`);
        this.infoIconPath.set(`${iconBase}/info_icon.svg`);
        this.openArrowIconPath.set(`${iconBase}/open_arrow.svg`);
        this.logoutIconPath.set(`${iconBase}/logout_icon.svg`);
        this.backMenuIconPath.set(`/assets/icons/light/ui/back_menu.svg`);
        this.welcomeIconPath.set(`${iconBase}/welcome_icon.svg`);
        this.feedbackIconPath.set(`${iconBase}/feedback_icon.svg`);
        this.tutorialIconPath.set(`${iconBase}/tutorial_icon.svg`);
        this.privacyPolicyIconPath.set(`${iconBase}/privacy_policy_icon.svg`);
        this.legalInfoIconPath.set(`${iconBase}/legal_info_icon.svg`);
      }
    }));

    this.subscriptions.add(this.accountService.ObservableIsAuthorized.subscribe((resp) => {
      if (resp != null && resp != undefined) {
        this.isAuthorized.set(resp);
        // Login and logout both leave the menu closed.
        this.closeRequested.emit();
      }
    }));

  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.outsidePressSubscription?.unsubscribe();
  }

  public openSubscriptionsDialog(): void {
    this.closeRequested.emit();
    this.stackedModalsService.openModal(SubscriptionsDialogComponent, {
      autoFocus: false,
      panelClass: 'dialog-snap-to-fullscreen'
    });
  }

  public openAccountDialog(): void {
    this.closeRequested.emit();
    const process = this.accountService.getIsAuthorized() ? 'profileEdit' : 'signIn';
    this.stackedModalsService.openModal(AccountDialogComponent, {
      data: { process: process },
      autoFocus: false,
      panelClass: 'dialog-snap-to-fullscreen'
    });
  }

  public openHistoryDialog(): void {
    this.closeRequested.emit();
    this.stackedModalsService.openModal(HistoryDialogComponent, {
      panelClass: 'dialog-snap-to-fullscreen',
      autoFocus: false
    });
  }

  public openSettings(): void {
    this.closeRequested.emit();
    this.stackedModalsService.openModal(SettingsDialogComponent, {
      autoFocus: false,
      panelClass: 'dialog-snap-to-fullscreen'
    });
  }

  public toggleInfoSubMenu(): void {
    this.infoSubMenuVisible.update(visible => !visible);
  }

  public closeInfoSubMenu(): void {
    this.infoSubMenuVisible.set(false);
  }

  public openInfoTab(tab: string): void {
    const componentMap: { [key: string]: any } = {
      welcome: WelcomeComponent,
      feedback: FeedbackComponent,
      privacy: PrivacyPolicyComponent,
      legal: EulaComponent
    };
    const component = componentMap[tab];
    if (!component) return;
    this.closeRequested.emit();
    this.stackedModalsService.openModal(component, {
      autoFocus: false,
      panelClass: 'dialog-snap-to-fullscreen'
    });
  }

  // The guided tutorial walks the user from a blank range screen through
  // planning and navigation, so it can only begin in RANGE mode; the menu
  // entry is greyed out elsewhere.
  public startTutorial(): void {
    if (this.tutorialService.start()) {
      this.closeRequested.emit();
    }
  }

  public logout(): void {
    this.closeRequested.emit();
    this.accountService.logout();
  }

  public login(): void {
    this.closeRequested.emit();
    this.stackedModalsService.openModal(AccountDialogComponent, {
      data: { process: 'signIn' },
      autoFocus: false,
      panelClass: 'dialog-snap-to-fullscreen'
    });
  }
}